/**
 * Visible-statements export (CSV + Markdown), reimplemented in React.
 *
 * Replaces the legacy `downloadStatements` / `downloadStatementsTextWithTopics`
 * (LeftMenuLogic.ejs:1544+ / 1689+) which read from `topicsOfStatement` —
 * a structure no longer populated under the new `new_3d_graph` graph
 * architecture. Threw `Cannot read properties of undefined (reading 'name')`.
 *
 * New source of truth:
 *   - statements: `window.filterStore.getState().filteredStatements`
 *   - topic name lookup: `useZustand.getState().allClusters` (id → name)
 *
 * Files are generated entirely in-memory and downloaded via a temporary
 * anchor — no DOM state, no `ExcellentExport`, no legacy globals.
 */

import { useZustand } from '../../hooks/useZustand'

function getTopicName(topCommunity) {
    if (topCommunity == null) return ''
    const clusters = useZustand.getState().allClusters || []
    const match = clusters.find(c => c.id === topCommunity)
    return match?.name || ''
}

function getCurrentContextLabel() {
    const z = useZustand.getState()
    return z.currentContext || 'context'
}

function getCurrentUser() {
    return (typeof userFactory !== 'undefined' && userFactory.getCurrentUser?.()) || 'user'
}

function visibleStatements() {
    const all = window.filterStore?.getState()?.filteredStatements || []
    return all.filter(s => s.grayedOut !== true && s.visible === true)
}

// RFC 4180-ish CSV cell escape: wrap in quotes if it contains a quote, comma,
// CR, or LF; double any embedded quotes.
function csvCell(value) {
    const s = value == null ? '' : String(value)
    if (/[",\r\n]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`
    }
    return s
}

export function downloadBlob(content, mime, filename) {
    const blob = new Blob([content], { type: mime })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    // Chrome resolves the blob URL asynchronously after click(); revoking in
    // the same task races the download and can yield a 0-byte file.
    setTimeout(() => URL.revokeObjectURL(url), 30000)
}

// Result envelope. Returning instead of calling `alert()` keeps this
// module a pure domain-logic concern; the React caller surfaces user
// feedback (snackbar, dialog, etc.).
const EMPTY_STATEMENTS_RESULT = {
    ok: false,
    error: 'No visible statements to export. Adjust your filters and try again.',
}

/**
 * CSV: Statement, Tags, Topical Cluster, Sentiment, Hashtags
 * (matches the legacy column set minus the broken AI-cluster + cluster-code
 * columns that depended on the dead `topicsOfStatement` map.)
 *
 * @returns {{ ok: boolean, error?: string }}
 */
export function downloadVisibleStatementsCsv() {
    const stmts = visibleStatements()
    if (stmts.length === 0) return EMPTY_STATEMENTS_RESULT

    const headers = ['Statement', 'Tags', 'Topical Cluster', 'Sentiment', 'Hashtags']
    const rows = stmts.map(s =>
        [
            s.text || '',
            (s.tags || []).join(','),
            getTopicName(s.topCommunity),
            s.sentiment || '',
            (s.hashtags || []).join(', '),
        ]
            .map(csvCell)
            .join(',')
    )
    const csv = [headers.map(csvCell).join(','), ...rows].join('\n')
    const filename = `${getCurrentUser()}_${getCurrentContextLabel()}_entries.csv`
    downloadBlob(csv, 'text/csv;charset=utf-8;', filename)
    return { ok: true }
}

/**
 * Markdown: simple `| Statement | Topical Cluster | Tags |` table — one
 * heading row, statements as rows. Suits Obsidian / general MD readers.
 *
 * @returns {{ ok: boolean, error?: string }}
 */
export function downloadVisibleStatementsMarkdown() {
    const stmts = visibleStatements()
    if (stmts.length === 0) return EMPTY_STATEMENTS_RESULT

    const escape = c => String(c || '').replace(/\|/g, '\\|').replace(/\n/g, ' ')
    const headerRow = `| ${getCurrentContextLabel()} | Topical Cluster | Tags |`
    const sepRow = `| --- | --- | --- |`
    const dataRows = stmts.map(s => {
        const text = escape(s.text)
        const cluster = escape(getTopicName(s.topCommunity))
        const tags = escape((s.tags || []).join(','))
        return `| ${text} | ${cluster} | ${tags} |`
    })

    const md = [headerRow, sepRow, ...dataRows].join('\n')
    const filename = `${getCurrentUser()}_${getCurrentContextLabel()}_entries.md`
    downloadBlob(md, 'text/markdown;charset=utf-8;', filename)
    return { ok: true }
}
